import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import {
  compareRidesByBoardPriority,
  fetchAllRides,
  purgeCompletedCancelledRides,
  formatRideAddress,
  formatRideDateParts,
  formatRideFare,
  getRideDisplayMarket,
  formatRidePhone,
  RIDE_STATUS_FILTERS,
  rideStatusLabel,
} from '../lib/rides';
import { useAdminRidesFeed } from '../hooks/useAdminRidesFeed';
import { isHireCarRide } from '../lib/ride-products';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const HIRE_PAGE_SIZES = [10, 15, 25];

const HIRE_KPI_FILTERS = [
  { value: 'open', label: 'Open requests', tone: 'open' },
  { value: 'assigned', label: 'Assigned', tone: 'assigned' },
  { value: 'live', label: 'In trip', tone: 'live' },
  { value: 'completed', label: 'Completed', tone: 'completed' },
  { value: 'cancelled', label: 'Cancelled', tone: 'cancelled' },
  { value: 'all', label: 'All trips', tone: 'all' },
];

function buildPageList(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

function AdminRidesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [purging, setPurging] = useState(false);

  const fetchAll = useCallback(() => fetchAllRides(supabase), []);

  const {
    rides,
    loading,
    error,
    setError,
    refresh,
    liveConnected,
    updatedAt,
  } = useAdminRidesFeed({
    enabled: Boolean(user),
    fetchRides: fetchAll,
  });

  const statusFilter = searchParams.get('status') || 'open';
  const search = searchParams.get('q') || '';
  const page = Math.max(1, Number(searchParams.get('page') || 1));
  const pageSizeParam = Number(searchParams.get('size') || 15);
  const pageSize = HIRE_PAGE_SIZES.includes(pageSizeParam) ? pageSizeParam : 15;

  const [searchDraft, setSearchDraft] = useState(search);

  const updateParams = useCallback(
    (patch) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([key, value]) => {
          if (value === '' || value == null) next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  useEffect(() => {
    const trimmed = searchDraft.trim();
    if (trimmed === search.trim()) return undefined;
    const timer = window.setTimeout(() => {
      updateParams({ q: trimmed, page: 1 });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchDraft, search, updateParams]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setSessionChecked(true);
      return undefined;
    }

    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const nextUser = data.session?.user || null;
      setUser(nextUser);
      setSessionChecked(true);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user || null;
      setUser(nextUser);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const hireRides = useMemo(() => rides.filter(isHireCarRide), [rides]);

  const filteredRides = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = hireRides.filter((ride) => {
      if (statusFilter !== 'all' && ride.boardStatus !== statusFilter) return false;
      if (!q) return true;
      const id = String(ride.id || '').toLowerCase();
      return (
        id.includes(q) ||
        ride.customer_name?.toLowerCase().includes(q) ||
        ride.customer_phone?.includes(q) ||
        ride.pickup_address?.toLowerCase().includes(q) ||
        ride.dropoff_address?.toLowerCase().includes(q) ||
        ride.assigned_driver_name?.toLowerCase().includes(q)
      );
    });
    list.sort(compareRidesByBoardPriority);
    return list;
  }, [hireRides, search, statusFilter]);

  const counts = useMemo(
    () =>
      hireRides.reduce(
        (acc, ride) => {
          acc.all += 1;
          acc[ride.boardStatus] = (acc[ride.boardStatus] || 0) + 1;
          return acc;
        },
        { all: 0, open: 0, assigned: 0, live: 0, completed: 0, cancelled: 0 }
      ),
    [hireRides]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRides.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRides.slice(start, start + pageSize);
  }, [filteredRides, currentPage, pageSize]);

  const rangeStart = filteredRides.length ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filteredRides.length);
  const pageList = buildPageList(currentPage, totalPages);

  const logout = async () => {
    await supabase?.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  const clearTestTrips = async () => {
    if (!supabase) return;
    const ok = window.confirm(
      'Delete ALL completed and cancelled trips from the app and website? This cannot be undone.'
    );
    if (!ok) return;

    setPurging(true);
    setError('');
    const result = await purgeCompletedCancelledRides(supabase);
    setPurging(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    await refresh({ showSpinner: false });
    if (result.total > 0) {
      window.alert(`Removed ${result.total} test trip(s) (${result.appDeleted} app, ${result.ridesDeleted} web).`);
    } else {
      window.alert('No completed or cancelled trips were found.');
    }
  };

  const clearSearch = () => {
    setSearchDraft('');
    updateParams({ q: '', page: 1 });
  };

  if (!sessionChecked) {
    return <div className="admin-loading">Checking admin session…</div>;
  }

  if (!user) return null;

  const activeFilterLabel =
    RIDE_STATUS_FILTERS.find((f) => f.value === statusFilter)?.label || 'All trips';

  const rideDetailPath = (ride) =>
    `/admin/rides/${ride.source}/${encodeURIComponent(ride.id)}`;

  return (
    <AdminShell
      userEmail={user.email}
      active="hire"
      onLogout={logout}
      actions={
        <>
          <button
            type="button"
            className="btn btn--ghost-dark"
            onClick={clearTestTrips}
            disabled={loading || purging}
          >
            {purging ? 'Removing…' : 'Clear test trips'}
          </button>
          <button
            type="button"
            className="btn btn--ghost-dark"
            onClick={() => refresh({ showSpinner: true })}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </>
      }
    >
      <div className="rides-page daily-dash hire-rides">
        <header className="admin-page-head admin-page-head--compact">
          <h1>
            Hire a car{' '}
            <span className="admin-page-head__hint">
              (Outstation and website bookings — tap a row to assign a driver)
            </span>
          </h1>
          {liveConnected ? (
            <p className="admin-live-pill" role="status">
              Live updates on
              {updatedAt
                ? ` · synced ${updatedAt.toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`
                : ''}
            </p>
          ) : null}
        </header>

        <div className="instant-kpis hire-kpis" aria-label="Trip totals — click to filter list">
          {HIRE_KPI_FILTERS.map((kpi) => (
            <button
              key={kpi.value}
              type="button"
              className={`instant-kpi instant-kpi--${kpi.tone} ${
                statusFilter === kpi.value ? 'instant-kpi--selected' : ''
              }`}
              aria-pressed={statusFilter === kpi.value}
              onClick={() =>
                updateParams({
                  status: kpi.value === 'open' ? '' : kpi.value,
                  page: 1,
                })
              }
            >
              <span className="instant-kpi__value">{counts[kpi.value] || 0}</span>
              <span className="instant-kpi__label">{kpi.label}</span>
            </button>
          ))}
        </div>

        {(counts.open || 0) > 0 ? (
          <p className="instant-callout hire-callout" role="status">
            <strong>{counts.open}</strong> open request{counts.open === 1 ? '' : 's'} waiting for a
            driver — select <em>Open requests</em> and open a trip to assign.
          </p>
        ) : null}

        {error ? <div className="form-alert form-alert--error admin-error">{error}</div> : null}

        <section className="instant-panel instant-panel--table">
          <div className="instant-panel__toolbar admin-drivers-toolbar">
            <div className="driver-search-field hire-rides__search">
              <span className="driver-search__label" id="hire-rides-search-label">
                Search trips
              </span>
              <label className="driver-search" aria-labelledby="hire-rides-search-label">
                <svg
                  className="driver-search__glyph"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M20 20l-3.5-3.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  className="driver-search__input"
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Rider, phone, address, driver, or trip ID…"
                  autoComplete="off"
                />
                {searchDraft.trim() ? (
                  <button
                    type="button"
                    className="driver-search__clear"
                    onClick={clearSearch}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                ) : null}
              </label>
            </div>
            <label className="driver-search__page">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => updateParams({ size: Number(e.target.value), page: 1 })}
                aria-label="Rows per page"
              >
                {HIRE_PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>per page</span>
            </label>
          </div>

          <p className="hire-rides__meta">
            <strong>{activeFilterLabel}</strong>
            {search.trim() ? (
              <>
                {' · '}
                Results for &ldquo;{search.trim()}&rdquo;
              </>
            ) : (
              <> · priority order (open trips first)</>
            )}
          </p>

          {loading ? (
            <p className="admin-empty instant-panel__empty">Loading hire trips…</p>
          ) : filteredRides.length === 0 ? (
            <p className="admin-empty instant-panel__empty">
              No trips match your filters.
              {hireRides.length === 0
                ? ' If riders already booked in the app, run supabase/fix-ride-board-app-bookings.sql.'
                : search.trim()
                  ? ' Try a different search or choose All trips.'
                  : ' Try another status above.'}
            </p>
          ) : (
            <>
              <div className="instant-table-wrap">
                <table className="instant-trips-table hire-trips-table">
                  <thead>
                    <tr>
                      <th scope="col">Rider</th>
                      <th scope="col">Route</th>
                      <th scope="col">Status</th>
                      <th scope="col">Scheduled</th>
                      <th scope="col">Source</th>
                      <th scope="col">Phone</th>
                      <th scope="col">Fare</th>
                      <th scope="col">Driver</th>
                      <th scope="col">
                        <span className="sr-only">Open</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((ride) => {
                      const when = formatRideDateParts(ride.scheduled_at || ride.created_at);
                      const market = getRideDisplayMarket(ride);
                      const pickup = formatRideAddress(ride.pickup_address);
                      const drop = formatRideAddress(ride.dropoff_address);
                      const driverLabel =
                        ride.assigned_driver_name ||
                        (ride.assigned_driver_id ? 'Assigned' : '—');
                      const detailPath = rideDetailPath(ride);
                      return (
                        <tr
                          key={`${ride.source}-${ride.id}`}
                          className="instant-trips-table__row admin-table-row"
                          tabIndex={0}
                          onClick={() => navigate(detailPath)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(detailPath);
                            }
                          }}
                        >
                          <td>
                            <strong>{ride.customer_name || 'Rider'}</strong>
                            <span className="instant-trips-table__id">
                              {String(ride.id).slice(0, 8)}…
                            </span>
                          </td>
                          <td className="instant-trips-table__route">
                            <span title={pickup}>{pickup}</span>
                            <span className="instant-trips-table__arrow" aria-hidden="true">
                              →
                            </span>
                            <span title={drop}>{drop}</span>
                          </td>
                          <td>
                            <span className={`ride-pill ride-pill--${ride.boardStatus}`}>
                              {rideStatusLabel(ride)}
                            </span>
                          </td>
                          <td className="instant-trips-table__when">
                            {when.date}
                            <br />
                            {when.time || 'TBD'}
                          </td>
                          <td>
                            <span className={`ride-source ride-source--${ride.source}`}>
                              {ride.source === 'app' ? 'App' : 'Website'}
                            </span>
                          </td>
                          <td>{formatRidePhone(ride.customer_phone, market)}</td>
                          <td>{formatRideFare(ride) || '—'}</td>
                          <td>{driverLabel}</td>
                          <td>
                            <Link
                              to={detailPath}
                              className="instant-trips-table__link hire-trips-table__link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Open
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <footer className="admin-drivers__footer">
                <p className="admin-drivers__summary">
                  Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of{' '}
                  <strong>{filteredRides.length}</strong> ({activeFilterLabel.toLowerCase()}
                  {search.trim() ? ` · “${search.trim()}”` : ''})
                </p>
                <nav className="rides-panel__pager" aria-label="Hire trip pages">
                  <button
                    type="button"
                    className="rides-panel__page-btn"
                    disabled={currentPage <= 1}
                    onClick={() => updateParams({ page: 1 })}
                  >
                    First
                  </button>
                  <button
                    type="button"
                    className="rides-panel__page-btn"
                    disabled={currentPage <= 1}
                    onClick={() => updateParams({ page: currentPage - 1 })}
                  >
                    Prev
                  </button>
                  {pageList.map((item, index) =>
                    item === '…' ? (
                      <span key={`gap-${index}`} className="rides-panel__ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`rides-panel__page-btn ${
                          item === currentPage ? 'rides-panel__page-btn--active' : ''
                        }`}
                        onClick={() => updateParams({ page: item })}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="rides-panel__page-btn"
                    disabled={currentPage >= totalPages}
                    onClick={() => updateParams({ page: currentPage + 1 })}
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    className="rides-panel__page-btn"
                    disabled={currentPage >= totalPages}
                    onClick={() => updateParams({ page: totalPages })}
                  >
                    Last
                  </button>
                </nav>
              </footer>
            </>
          )}
        </section>
      </div>
    </AdminShell>
  );
}

export default AdminRidesPage;
